import React from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Building,
  Mail,
  Phone,
  MapPin,
  Globe,
  Lock,
  Eye,
  FileText,
  Users,
  Clock,
  AlertCircle,
} from "lucide-react";

const Policy = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Privacy Policy
          </h1>
          <div className="bg-white rounded-lg shadow-lg p-6 text-left max-w-2xl mx-auto">
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>Effective date:</strong> 24 April 2026
              </p>
              <p>
                <strong>Last updated:</strong> 24 April 2026
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Who We Are */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 flex items-center">
            <Building className="w-8 h-8 mr-3 text-acBlack" strokeWidth={1} />
            Who we are
          </h2>

          <div className="bg-gray-50 rounded-lg p-8 mb-8">
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Operating business name:</strong> Notapaths
              </p>
              <p>
                <strong>Legal entity / Data controller (UK & EEA):</strong>{" "}
                JONOTA TRAVEL AGENCY LIMITED (trading as "Notapaths")
              </p>
              <p>
                <strong>Company number:</strong> 10212977 (England & Wales)
              </p>
              <p>
                <strong>Registered office:</strong> 8a Hessel Street, Ground
                Floor, London, United Kingdom, E1 2LP
              </p>
              <p>
                <strong>Website:</strong> notapaths.com
              </p>
              <p>
                <strong>Privacy contact:</strong> privacy@notapaths.com
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-gray-700">
              We have not appointed a statutory Data Protection Officer. For all
              privacy matters, contact our privacy team at{" "}
              <span className="font-semibold">privacy@notapaths.com</span>.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Offices & Contact */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            Office & contact
          </h2>

          <div className="mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-acBlack mb-4 flex items-center">
                <MapPin className="w-6 h-6 mr-2 text-acBlack" strokeWidth={1} />
                London Office
              </h3>
              <div className="space-y-2 text-gray-700">
                <p>
                  8a Hessel Street, Ground Floor, London, United Kingdom, E1 2LP
                </p>
                <p>+44 7418 622985 · info@notapaths.com</p>
                <p>
                  <strong>Hours:</strong> Mon–Fri, 09:00–18:00 GMT/BST
                </p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-gray-700">
              <strong>EU representative (Article 27 GDPR):</strong> If
              appointed, details will be added here and in our website footer.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Scope */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            1) Scope
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            This policy explains how we collect, use, disclose, store, and
            protect personal data when you:
          </p>
          <ul className="space-y-2 text-gray-700 mb-6">
            <li>• visit our websites or social pages;</li>
            <li>• submit a form or book a consultation;</li>
            <li>• engage us for immigration services;</li>
            <li>• receive updates or marketing communications; or</li>
            <li>• apply for a role with us.</li>
          </ul>
          <p className="text-gray-700">
            This policy does not cover third-party websites or services we link
            to.
          </p>
        </div>
      </motion.section>

      {/* The Data We Collect */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            2) The data we collect
          </h2>

          <div className="space-y-6">
            {[
              {
                category: "A) Identity & contact",
                description:
                  "name, aliases, date/place of birth, nationality, passport/ID numbers, photos, signatures, addresses, phone numbers, email addresses.",
                icon: <Users className="w-6 h-6" strokeWidth={1} />,
              },
              {
                category: "B) Case & eligibility",
                description:
                  "CV/résumé, education, employment history, job offers/contracts, salary/tax data, licences, study offers/enrolment, sponsorship/invitations.",
                icon: <FileText className="w-6 h-6" strokeWidth={1} />,
              },
              {
                category: "C) Family & relationships",
                description:
                  "marital/partnership status, spouse/partner/children details, birth/marriage records, custody/consent documents.",
                icon: <Users className="w-6 h-6" strokeWidth={1} />,
              },
              {
                category: "D) Immigration & travel",
                description:
                  "prior visas/permits, refusals, travel history, entry/exit stamps, I-94 or equivalents, biometrics notices, case numbers, decision letters.",
                icon: <Globe className="w-6 h-6" strokeWidth={1} />,
              },
              {
                category: "E) Finance & documents",
                description:
                  "bank statements, proof of funds, sponsorship declarations, company and ownership records, source-of-funds/wealth (e.g., investor routes).",
                icon: <FileText className="w-6 h-6" strokeWidth={1} />,
              },
              {
                category: "F) Sensitive data (special category/criminal)",
                description:
                  "health info for medicals (e.g., TB tests), disability accommodations, police certificates, and data revealing racial/ethnic origin or religion where present on official records. We collect this only where necessary and lawful.",
                icon: <Shield className="w-6 h-6" strokeWidth={1} />,
              },
              {
                category: "G) Technical & usage",
                description:
                  "device identifiers, IP address, country inferred from IP, pages visited, referring URLs, timestamps, cookies/pixels for analytics and session management.",
                icon: <Eye className="w-6 h-6" strokeWidth={1} />,
              },
              {
                category: "H) Communications",
                description:
                  "emails, messages, call notes, WhatsApp/chat transcripts (if you choose to use them), and feedback.",
                icon: <Mail className="w-6 h-6" strokeWidth={1} />,
              },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-start">
                  <div className="bg-yellow-100 p-2 rounded-lg mr-4 text-acBlack flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-acBlack mb-2">
                      {item.category}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Where We Get Your Data */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            3) Where we get your data
          </h2>
          <ul className="space-y-3 text-gray-700">
            <li>• Directly from you or someone you authorise;</li>
            <li>• Your employer/prospective employer, school, or sponsor;</li>
            <li>
              • Government bodies and visa centres where you ask us to interact;
            </li>
            <li>
              • Public sources and verification providers (where appropriate);
            </li>
            <li>
              • Service providers (e-signature, scheduling, payments,
              translation, identity verification).
            </li>
          </ul>
        </div>
      </motion.section>

      {/* How We Use Your Data */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            4) How we use your data & lawful bases
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            We process personal data only where a lawful basis applies.
          </p>

          <div className="space-y-6">
            {[
              {
                purpose: "A) Providing services / contract",
                description:
                  "eligibility assessments, strategy, document preparation, bookings, submissions (where permitted), status tracking, and guidance.",
                basis:
                  "performance of a contract or steps prior to a contract.",
              },
              {
                purpose: "B) Compliance with law",
                description:
                  "record-keeping, tax/accounting, sanctions and identity checks, lawful requests.",
                basis: "legal obligation.",
              },
              {
                purpose: "C) Legitimate interests",
                description:
                  "client relationship management, improving services, preventing fraud/abuse, security of our systems, audits, business continuity.",
                basis: "legitimate interests not overridden by your rights.",
              },
              {
                purpose: "D) Consent",
                description:
                  "optional marketing, testimonials, and certain uses where required.",
                basis: "your consent (you can withdraw at any time).",
              },
              {
                purpose: "E) Vital interests",
                description: "urgent circumstances affecting health or safety.",
                basis: "vital interests.",
              },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-acBlack mb-2">
                  {item.purpose}
                </h3>
                <p className="text-gray-700 text-sm mb-2">{item.description}</p>
                <p className="text-gray-600 text-sm">
                  <strong>Basis:</strong> {item.basis}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
            <h3 className="font-semibold text-acBlack mb-3">
              Special category & criminal-history data (only where needed for
              immigration work):
            </h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>
                • Legal claims & advice (establishment, exercise, defence of
                claims);
              </li>
              <li>• Substantial public interest where permitted by law;</li>
              <li>
                • Explicit consent where required (withdrawal may affect our
                ability to act).
              </li>
            </ul>
          </div>
        </div>
      </motion.section>

      {/* Automated Decisions */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            5) Automated decisions & profiling
          </h2>
          <p className="text-gray-700 leading-relaxed">
            We do not make decisions with legal or similarly significant effects
            based solely on automated processing. Government authorities may use
            automated tools in their own systems; we do not control those
            systems.
          </p>
        </div>
      </motion.section>

      {/* Sharing Your Data */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            6) Sharing your data
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We share data only for the purposes described and with appropriate
            safeguards:
          </p>

          <ul className="space-y-3 text-gray-700 mb-8">
            <li>
              • Licensed local counsel where regulated representation is
              required;
            </li>
            <li>• Translation/legalisation providers and document couriers;</li>
            <li>
              • Medical panels and police certificate bodies at your
              instruction;
            </li>
            <li>
              • Government authorities and visa centres to submit/manage
              applications;
            </li>
            <li>
              • Technology vendors acting as processors (secure cloud storage,
              CRM, email, scheduling, e-signature, ticketing, accounting);
            </li>
            <li>• Payment processors for billing;</li>
            <li>• Professional advisers/auditors;</li>
            <li>
              • Successors in a merger, acquisition, or reorganisation (with
              safeguards).
            </li>
          </ul>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <p className="text-gray-700">
              <strong>We do not sell personal data</strong> and we do not share
              it for cross-context behavioural advertising.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-gray-700">
              <strong>Controller vs processor.</strong> When engaged directly by
              you, we are a controller. If we deliver services to you on behalf
              of your employer/sponsor under their instructions, we may act as a
              processor and will process your data according to that contract.
            </p>
          </div>
        </div>
      </motion.section>

      {/* International Transfers */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            7) International transfers
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            To deliver services, we may transfer data internationally to
            countries relevant to your immigration matter (for example, your
            destination country, consulates or visa centres handling your
            application, and our service providers).
          </p>

          <p className="text-gray-700 leading-relaxed mb-4">
            For transfers from the UK/EEA to countries without an adequacy
            decision, we use appropriate safeguards such as:
          </p>
          <ul className="space-y-2 text-gray-700 mb-6">
            <li>
              • Standard Contractual Clauses (EU SCCs) and/or the UK Addendum;
            </li>
            <li>
              • vendor participation in the EU-US/UK-US Data Privacy Framework
              (where applicable);
            </li>
            <li>
              • additional technical and organisational measures (encryption,
              access controls).
            </li>
          </ul>

          <p className="text-gray-700">
            Request copies of applicable safeguards at{" "}
            <span className="font-semibold">privacy@notapaths.com</span>.
          </p>
        </div>
      </motion.section>

      {/* Data Retention */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 flex items-center">
            <Clock className="w-8 h-8 mr-3 text-acBlack" strokeWidth={1} />
            8) Data retention
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We retain data only as long as necessary for the purposes in this
            policy and to meet legal/regulatory requirements. Typical periods:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                period: "Client matter files",
                duration:
                  "6 years after case closure (longer if required for legal claims)",
              },
              {
                period: "Accounting & tax",
                duration: "7 years from the end of the financial year",
              },
              {
                period: "KYC & sanctions checks",
                duration:
                  "5 years from the end of the client relationship (or as required by law)",
              },
              {
                period: "Marketing contacts",
                duration:
                  "until you opt out, then minimal suppression data for up to 2 years",
              },
              {
                period: "Recruitment applications",
                duration:
                  "12 months if not hired (or as required by local law)",
              },
              {
                period: "Messaging transcripts",
                duration:
                  "up to 2 years from case closure (longer if required for compliance)",
              },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-acBlack mb-2">
                  {item.period}
                </h4>
                <p className="text-gray-600 text-sm">{item.duration}</p>
              </div>
            ))}
          </div>

          <p className="text-gray-700 mt-6">
            If you request deletion, we'll comply subject to legal limits and
            explain any records we must retain.
          </p>
        </div>
      </motion.section>

      {/* Security */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 flex items-center">
            <Lock className="w-8 h-8 mr-3 text-acBlack" strokeWidth={1} />
            9) Security
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We apply layered safeguards proportionate to risk, including:
          </p>
          <ul className="space-y-2 text-gray-700 mb-6">
            <li>• role-based/least-privilege access</li>
            <li>• encryption in transit and at rest (where supported)</li>
            <li>• MFA for key systems</li>
            <li>• backups and continuity planning</li>
            <li>• vendor due diligence and confidentiality obligations</li>
            <li>• audit logging</li>
            <li>• employee training</li>
          </ul>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
            <p className="text-gray-700">
              No system is perfectly secure. If we become aware of an incident
              affecting your data, we will act promptly and, where required by
              law, notify you and relevant authorities without undue delay.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Your Rights */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            10) Your rights
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Depending on your location, you may have rights to:
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              "access your data",
              "rectify inaccurate/incomplete data",
              "erase data in certain cases",
              "restrict processing in certain cases",
              "data portability (machine-readable copy of data you provided)",
              "object to processing based on legitimate interests or to direct marketing",
              "withdraw consent where processing relies on consent",
              "complain to a supervisory authority",
            ].map((right, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-gray-700 text-sm">• {right}</p>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <p className="text-gray-700">
              <strong>How to exercise your rights:</strong> email
              privacy@notapaths.com. We may need to verify your identity and
              clarify the request. We aim to respond within one month, or inform
              you if more time is needed due to complexity.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-acBlack">
              Supervisory authorities
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li>
                <strong>UK:</strong> Information Commissioner's Office
                (ico.org.uk)
              </li>
              <li>
                <strong>EEA:</strong> your local Data Protection Authority (see
                edpb.europa.eu)
              </li>
              <li>
                <strong>US:</strong> your state attorney general or relevant
                federal agency
              </li>
            </ul>
          </div>
        </div>
      </motion.section>

      {/* Quick Summary */}
      <motion.section className="py-16 px-6 bg-blue-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            18) Quick summary for clients
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "We collect only what's needed to deliver immigration services and meet legal duties.",
              "We never sell your personal data.",
              "We share data only with authorities, licensed counsel, and trusted providers-under safeguards.",
              "You control marketing choices and can exercise privacy rights at any time.",
              "We keep data only as long as needed and protect it with layered security.",
            ].map((point, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-start">
                  <Shield
                    className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0"
                    strokeWidth={1}
                  />
                  <p className="text-gray-700">{point}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Contact */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            17) Contact
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="font-semibold text-acBlack mb-3">
                Email (privacy)
              </h3>
              <a
                href="mailto:privacy@notapaths.com"
                className="text-acBlack hover:text-gray-700 transition-colors"
              >
                privacy@notapaths.com
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="font-semibold text-acBlack mb-3">UK postal</h3>
              <p className="text-gray-700 text-sm">
                JONOTA TRAVEL AGENCY LIMITED (t/a Notapaths), 8a Hessel Street,
                Ground Floor, London, United Kingdom, E1 2LP
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="font-semibold text-acBlack mb-3">London Office</h3>
              <p className="text-gray-700 text-sm">
                8a Hessel Street, Ground Floor, London, E1 2LP, UK
                <br />
                +44 7418 622985
                <br />
                info@notapaths.com
              </p>
            </div>

            {/* <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
             <p className="text-gray-700">
               <strong>EU representative:</strong> If appointed, details will appear here.
             </p>
           </div> */}
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Policy;
